/* import __COLOCATED_TEMPLATE__ from './project-cancellation-payment.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { dropTask, restartableTask } from "ember-concurrency";
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { showModal } from "sh-www/utils/modal-controls";
import { debounce } from '@ember/runloop';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { makeMoney } from 'sh-www/helpers/make-money';
import { APPLICATION } from 'sh-www/models/application';
import { SH_BILLABLE_LINE_ITEM } from 'sh-www/models/sh-billable-line-item';
import { BILLABLE_RECORD } from 'sh-www/models/billable-record';
import { PROJECT_CANCELLATION__CANCELLATION_REASONS } from "sh-www/constants/routes/project-cancellation";

const CLASS_NAME = 'SH-c-project-cancellation-payment';

export default class ProjectCancellationPayment extends Component {
  @service('commands/application-commands') applicationCommandService;
  @service('state-managers/application') applicationStateManager;
  @service moment;
  @service router;
  @service session;
  @service shData;
  @service store;

  @tracked testBillingRecord = undefined;
  @tracked cancellationAmount = '';
  @tracked cancellationAmountError = '';
  @tracked expedite = false;

  get className() {
    return CLASS_NAME;
  }

  /** @type {Number} */
  get baseNonExpenseAmount() {
    const isBuyerViewing = this.session.currentUser.isBuyer;
    const baseBillable = isBuyerViewing ? this.args.application.baseShInvoice : this.args.application.baseShPayment;
    return baseBillable.nonFeesAmount;
  }

  /** @type {String} Dynamic text for the cancellation overview text for the buyer. */
  get cancellationText() {
    const buyerDueAlreadyAmount = this.buyerDueAdvancesAmountNoFee;

    if (buyerDueAlreadyAmount) {
      return `You have ${makeMoney([
        buyerDueAlreadyAmount,
      ])} due so far. What are you offering as a remaining payout to ${
        this.args.application.seller.firstName
      }?`;
    }

    return `What are you offering as a remaining payout to ${this.args.application.seller.firstName}?`;
  }

  /** @type {String} Text to display to the buyer after submitting. */
  get cancellationSubmittedText() {
    const sellerFirstName = this.args.application.seller.firstName;
    return `We notified ${sellerFirstName} of your request to cancel the project.`;
  }

  /** @type {Boolean} Whether the cancellation is in the pending or submitted state. */
  get isCancellationRequestSubmitted() {
    return this.args.application.status === APPLICATION.STATUS.CANCEL_PENDING;
  }

  /** @type {Boolean} Whether the send cancellation button is disabled. */
  get isRequestCancellationButtonDisabled() {
    if (this.cancellationAmount === '') return true;

    return (
      this.updateTestBillingRecord.isRunning
      || this.sendCancellationRequest.isRunning
      || this.confirmCancellation.isRunning
      || this.cancellationPriceRangErrorText
    );
  }

  get buyerDueAdvancesAmountNoFee() {
    const invoicesToAdd = this.args.application.shInvoices;
    const lineItemFeeTypes = new Set(SH_BILLABLE_LINE_ITEM.LINE_ITEM_FEE_TYPES);
    if (!invoicesToAdd) {
      return 0;
    }

    // this is just adding up all the line item amounts for all the invoices
    return invoicesToAdd.reduce((invoiceCarry, shInvoice) => {
      if (shInvoice.type !== BILLABLE_RECORD.TYPES.PROJECT_ADVANCE || !shInvoice.dueAt) {
        return invoiceCarry;
      }

      return invoiceCarry + shInvoice.shInvoiceLineItems.reduce((carry, shInvoiceLineItem) => {
        if (lineItemFeeTypes.has(shInvoiceLineItem.type)) {
          return carry;
        }

        return carry + shInvoiceLineItem.amount;
      }, 0);
    }, 0);
  }

  /** @type {Number} The remaining payout for the seller. */
  get buyerRemainingPayoutForSeller() {
    const expensesAmount = this.args.application.expensesTotalAmount;
    const dueAdvancesAmount = this.buyerDueAdvancesAmountNoFee;
    const cancellationAmount = this.cancellationAmount
      ? parseFloat(this.cancellationAmount)
      : 0;

    return Math.max(expensesAmount - dueAdvancesAmount, cancellationAmount);
  }

  /** @type {String} The prompt for the buyer to enter a valid final payment amount. */
  get cancellationPriceRangErrorText() {
    const dueAdvancesAmount = this.buyerDueAdvancesAmountNoFee;
    const expensesAmount = this.args.application.expensesTotalAmount;
    const cancellationAmount = this.cancellationAmount
      ? parseFloat(this.cancellationAmount)
      : 0;

    // If we have more expenses than what the user's cancellation amount is, show the error text.
    if (expensesAmount - dueAdvancesAmount > cancellationAmount) {
      return `You have expenses of ${makeMoney([expensesAmount])}. You will need to offer at least ${makeMoney([this.buyerRemainingPayoutForSeller])}.`;
    }

    return '';
  }

  /** Fetches the test billing record from the server. */
  @restartableTask
  *updateTestBillingRecord() {
    const isBuyerViewing = this.session.currentUser.isBuyer;
    this.cancellationAmountError = '';

    try {
      const response = yield this.applicationCommandService.getTestBillingRecord({
        application: this.args.application,
        expedite: this.expedite,
        userType: isBuyerViewing ? 'buyer' : 'seller',
        cancellationAmount: this.cancellationAmount ? this.cancellationAmount : null,
      });

      this.shData.pushResponseToStore(response);
      const modelType = isBuyerViewing ? 'shInvoice' : 'shPayment';
      this.testBillingRecord = this.store.peekRecord(modelType, response.data.id);
    } catch (error) {
      this.cancellationAmountError = Object.values(error.errors)?.[0]?.[0];
      this.testBillingRecord = undefined;
    }
    this.expedite = this.testBillingRecord?.expedite ?? false;
  }

  @dropTask
  *sendCancellationRequest() {
    // Pre-conditions
    if (!Object.values(PROJECT_CANCELLATION__CANCELLATION_REASONS).includes(this.args.projectCancellationReason)) {
      showModal(this, 'alert', {
        titleText: 'Error',
        bodyText: 'Please go back and select a valid reason for cancelling the project.',
        actionButtonText: 'OK',
      });
      return;
    }

    try {
      // Deal with changes to expedite status
      const baseShInvoice = this.args.application.baseShInvoice;
      if (!baseShInvoice.expedite && this.expedite) {
        // Mark expedited
        yield this.applicationCommandService.expeditePayout(this.args.application);
      } else if (
        baseShInvoice.expedite
        && baseShInvoice.expediteRequestor.id === this.session.currentUser.id
        && !this.expedite
      ) {
        // Un-mark expedited
        yield this.applicationCommandService.withdrawExpeditedPayout(this.args.application);
      }

      yield this.applicationCommandService.cancelApplication(
        this.args.application,
        this.cancellationAmount,
        this.args.projectCancellationReason,
        this.args.additionalInformationNotes,
      );

      this.applicationStateManager.addToast('Cancellation request submitted');
      this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__DETAILS, this.args.application.id);
    } catch (error) {
      handleErrorResponseWithModal(this, error);
    }
  }

  @dropTask
  *confirmCancellation() {
    try {
      // Deal with changes to expedite status
      const baseShPayment = this.args.application.baseShPayment;
      if (!baseShPayment?.expedite && this.expedite) {
        // Mark expedited
        yield this.applicationCommandService.expeditePayout(this.args.application);
      } else if (
        baseShPayment?.expedite
        && baseShPayment?.expediteRequestor?.id === this.session.currentUser.id
        && !this.expedite
      ) {
        // Un-mark expedited
        yield this.applicationCommandService.withdrawExpeditedPayout(this.args.application);
      }

      // Confirm the cancellation
      yield this.applicationCommandService.cancelApplication(
        this.args.application,
        null,
        '',
        '',
        true
      );
      this.applicationStateManager.addToast('Cancellation confirmed');
      this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__DETAILS, this.args.application.id);
    } catch (error) {
      handleErrorResponseWithModal(this, error);
    }
  }

  @action
  onBackClicked() {
    this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__CANCELLATION__DETAILS, this.args.application.id);
  }

  @action
  onDoneClicked() {
    this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__DETAILS, this.args.application.id);
  }

  @action
  handleExpediteClick(isExpedited) {
    this.expedite = isExpedited;
    this.getTestBillingRecord();
  }

  @action
  onInputCancellationAmount(amount) {
    this.cancellationAmount = amount;
    debounce(this, this.getTestBillingRecord, 750);
  }

  @action
  getTestBillingRecord() {
    return this.updateTestBillingRecord.perform();
  }
}
